import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import styles from './ServicePillars.module.css';

const images = [
    {
        src: '/images/change_management_hero.png',
        webp: '/images/change_management_hero-800.webp 800w, /images/change_management_hero-1600.webp 1600w',
    },
    {
        src: '/images/ai_automation_hero.png',
        webp: '/images/ai_automation_hero-800.webp 800w, /images/ai_automation_hero-1600.webp 1600w',
    },
    {
        src: '/images/erp_sap_hero.png',
        webp: '/images/erp_sap_hero-800.webp 800w, /images/erp_sap_hero-1600.webp 1600w',
    },
    {
        src: '/images/software_engineering_hero.png',
        webp: '/images/software_engineering_hero-800.webp 800w, /images/software_engineering_hero-1600.webp 1600w',
    },
];

export const ServicePillars: React.FC = () => {
    const { t, language } = useLanguage();

    const pillars = [
        { ...t.services.pillars.strategy, link: '/services/strategy-and-transformation' },
        { ...t.services.pillars.ai, link: '/services/ai-automation' },
        { ...t.services.pillars.erp, link: '/services/erp-sap' },
        { ...t.services.pillars.software, link: '/services/software-engineering' },
    ];

    const learnMoreText = language === 'en' ? 'Learn more' : 'Mehr erfahren';

    return (
        <section className={styles.pillars}>
            <div className={styles.pillars__container}>
                <div className={styles.pillars__grid}>
                    {pillars.map((pillar, index) => {
                        const image = images[index] ?? images[0];
                        return (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-50px' }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                            >
                                <Link to={pillar.link} className={styles.pillars__card}>
                                    <div className={styles.pillars__imageWrapper}>
                                        <picture>
                                            <source
                                                type="image/webp"
                                                srcSet={image.webp}
                                                sizes="(max-width: 768px) 100vw, 260px"
                                            />
                                            <img
                                                src={image.src}
                                                alt={pillar.title}
                                                className={styles.pillars__image}
                                                loading="lazy"
                                            />
                                        </picture>
                                    </div>
                                    <h3 className={styles.pillars__title}>{pillar.title}</h3>
                                    <ul className={styles.pillars__list}>
                                        {pillar.items.map((item: string, i: number) => (
                                            <li key={i} className={styles.pillars__item}>
                                                <span className={styles.pillars__bullet}>•</span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                    <span className={styles.pillars__link}>
                                        {learnMoreText} <ArrowRight size={16} />
                                    </span>
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
